import { Component, Fragment } from 'react';
import { List, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { DRAG_ITEM_TYPES } from 'src/constants/dragItem.ts';
import {
  API_RESULT_LIMITS,
  SEARCH_FORM_SETTINGS,
} from 'src/constants/settings.ts';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import ListWithSearch from 'src/components/common/ListWithSearch';

import WorkspaceVariableListItem from './components/WorkspaceVariableListItem';

export const WORKSPACE_VARIABLE_LIST_TEST_ID = 'workspace-variable-list';

class WorkspaceVariableList extends Component {
  state = {
    initialShapings: [],
    initialVariables: null,
    inputTimeout: null,
    currentlyLoadingQueries: [],
    currentlySearchingQueries: [],
    variables: [],
  };

  http = HttpService;

  componentDidMount() {
    this.getVariables();
  }

  /**
   * @description get variables for dataline filters.
   * @return {*} - returns a promise but only for the purpose of testing
   */
  getVariables = () => {
    const { variableTypeId, moduleId } = this.props;
    const requestEndpoint = apiPaths.VARIABLE_BY_TYPE + variableTypeId;

    return this.http
      .get(requestEndpoint, {
        limit: API_RESULT_LIMITS.SEARCH,
        module_id: moduleId,
      })
      .then(this.handleVariableResponse)
      .catch(this.handleError);
  };

  /**
   * @description get shapings for dataline variables.
   * @return {*} - returns a promise but only for the purpose of testing
   */
  getShapings = (variableId, query = null) => {
    const { moduleId } = this.props;
    const requestEndpoint = query
      ? `${apiPaths.SHAPING_BY_VARIABLE_ID}${variableId}&name=${query}`
      : apiPaths.SHAPING_BY_VARIABLE_ID + variableId;

    return this.http
      .get(requestEndpoint, {
        limit: API_RESULT_LIMITS.SEARCH,
        module_id: moduleId,
      })
      .then((response) => this.handleShapingResponse(response, variableId))
      .catch(this.handleError);
  };

  /**
   * @description Handles the variable endpoint response.
   * @param response
   */
  handleVariableResponse = (response) => {
    const {
      data: { results: variables },
    } = response;

    if (!this.state.initialVariables) {
      this.setState({ initialVariables: [...variables] });
    }

    this.setState({ variables });
  };

  /**
   * @description Handles the shaping endpoint response.
   * @param response
   * @param variableId
   */
  handleShapingResponse = (response, variableId) => {
    const {
      data: { results: shapings },
    } = response;
    const { variables, currentlyLoadingQueries, initialShapings } = this.state;

    // Add shapings to the related variable object.
    variables.find((variable) => variable.variable_id === variableId).shapings =
      shapings;

    // Save the initialShapings in the state so we can reset them when handleShapingSearch is being called without query
    if (!initialShapings[variableId]) {
      initialShapings[variableId] = shapings;
    }

    this.setState({
      variables,
      currentlyLoadingQueries: currentlyLoadingQueries.filter(
        (item) => item !== variableId,
      ),
    });
  };

  /**
   * @description this method handles the search of a shaping if a query string was provided. If no querystring was
   * provided it restores the previous variable list.
   * @param {string} queryString
   * @param {string} variableId
   */
  handleShapingSearch = (queryString, variableId) => {
    const fetchSearchResults = () => {
      const { currentlyLoadingQueries, currentlySearchingQueries } = this.state;
      const updatedCurrentlyLoadingQueries = currentlyLoadingQueries;
      const updatedCurrentlySearchingQueries = currentlySearchingQueries;

      updatedCurrentlyLoadingQueries.push(variableId);
      updatedCurrentlySearchingQueries.push(variableId);

      this.setState({
        currentlyLoadingQueries: updatedCurrentlyLoadingQueries,
        currentlySearchingQueries: updatedCurrentlySearchingQueries,
      });

      this.getShapings(variableId, queryString);
    };

    // Clear a possible input delay timeout on every input update.
    clearTimeout(this.state.inputTimeout);

    if (queryString) {
      this.setState({
        inputTimeout: setTimeout(
          fetchSearchResults,
          SEARCH_FORM_SETTINGS.DEBOUNCE_TIME,
        ),
      });
    } else {
      const {
        currentlyLoadingQueries,
        currentlySearchingQueries,
        initialShapings,
        variables,
      } = this.state;
      const loadingVariableIds = currentlyLoadingQueries.filter(
        (item) => item !== variableId,
      );
      const searchingVariableIds = currentlySearchingQueries.filter(
        (item) => item !== variableId,
      );

      variables.find(
        (variable) => variable.variable_id === variableId,
      ).shapings = initialShapings[variableId];

      this.setState({
        currentlyLoadingQueries: loadingVariableIds,
        currentlySearchingQueries: searchingVariableIds,
        variables,
      });
    }
  };

  render() {
    const {
      variables,
      currentlyLoadingQueries,
      currentlySearchingQueries,
      initialVariables,
    } = this.state;

    const renderShapingsAsListItems = (
      shapings,
      variableId,
      variableName,
      variableShortName,
    ) => (
      <>
        {shapings.length ? (
          shapings.map((shaping) => {
            const { shaping_id: shapingId, name: shapingName } = shaping;

            return (
              <WorkspaceVariableListItem
                dragSourceType={DRAG_ITEM_TYPES.SHAPING}
                id={shapingId}
                key={shapingId}
                shapingId={shapingId}
                title={shapingName}
                variableId={variableId}
                variableName={variableName}
                variableShortName={variableShortName}
              />
            );
          })
        ) : (
          <Typography sx={{ pb: 1 }}>No results found.</Typography>
        )}
      </>
    );

    return (
      <Fragment>
        <List data-test-id={WORKSPACE_VARIABLE_LIST_TEST_ID}>
          {variables.map((variable, index) => {
            const {
              name: variableName,
              shapings,
              variable_id: variableId,
              short_name: variableShortName,
            } = variable;
            const initialShapingCount =
              initialVariables[index].shaping_set.length;

            return (
              <WorkspaceVariableListItem
                clickHandler={this.getShapings}
                dragSourceType={DRAG_ITEM_TYPES.VARIABLE}
                collapsible
                id={variableId}
                key={variableId}
                shapings={shapings}
                shapingSet={variable.shaping_set}
                title={variableName}
                variableId={variableId}
                variableShortName={variableShortName}
              >
                {shapings && (
                  <>
                    {initialShapingCount >=
                    SEARCH_FORM_SETTINGS.SHAPING_THRESHOLD ? (
                      <ListWithSearch
                        handleSearch={this.handleShapingSearch}
                        isLoading={currentlyLoadingQueries.includes(variableId)}
                        isSearching={currentlySearchingQueries.includes(
                          variableId,
                        )}
                        searchParameter={variableId}
                        searchInputPlaceholder={
                          `${this.props.searchInputPlaceholderFirst} ${variableName}` +
                          ` ${this.props.searchInputPlaceholderValue}`
                        }
                      >
                        {renderShapingsAsListItems(
                          shapings,
                          variableId,
                          variableName,
                          variableShortName,
                        )}
                      </ListWithSearch>
                    ) : (
                      <List>
                        {renderShapingsAsListItems(
                          shapings,
                          variableId,
                          variableName,
                          variableShortName,
                        )}
                      </List>
                    )}
                  </>
                )}
              </WorkspaceVariableListItem>
            );
          })}
        </List>
      </Fragment>
    );
  }
}

WorkspaceVariableList.propTypes = {
  moduleId: PropTypes.number.isRequired,
  variableTypeId: PropTypes.number.isRequired,
  searchInputPlaceholderFirst: PropTypes.string.isRequired,
  searchInputPlaceholderValue: PropTypes.string.isRequired,
};

export default WorkspaceVariableList;
